import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import type { QuoteStatus } from '@/lib/quotes';

/**
 * Public (customer-facing) quote access. Everything goes through the two
 * SECURITY DEFINER RPCs keyed by the quote's unguessable token — anon has no
 * table access to quotes at all.
 */

export interface PublicQuoteLine {
  id: string;
  description: string;
  quantity: number;
  unit_amount: number;
  line_total: number;
  is_optional: boolean;
  is_selected: boolean;
}

export interface PublicQuote {
  quote_number: string;
  status: QuoteStatus;
  client_name: string;
  client_address: string | null;
  frequency: string | null;
  notes: string | null;
  valid_until: string | null;
  currency_code: string;
  vat_rate: number;
  subtotal_amount: number;
  vat_amount: number;
  total_amount: number;
  sent_at: string | null;
  created_at: string;
  accepted_at: string | null;
  accepted_name: string | null;
  declined_at: string | null;
  business: { name: string | null; logo_url: string | null; phone: string | null; vat_number: string | null };
  lines: PublicQuoteLine[];
}

type QuoteRpcClient = {
  rpc(name: 'get_public_quote', args: { _token: string }): Promise<{ data: unknown; error: { message: string } | null }>;
  rpc(
    name: 'respond_to_quote',
    args: { _token: string; _action: 'accept' | 'decline'; _name?: string | null; _reason?: string | null; _selected_line_ids?: string[] },
  ): Promise<{ data: unknown; error: { message: string } | null }>;
};

const rpcClient = supabase as unknown as QuoteRpcClient;

export const TOKEN_RE = /^[0-9a-f]{64}$/;

const num = (v: unknown) => (Number.isFinite(Number(v)) ? Number(v) : 0);

function normalise(raw: unknown): PublicQuote | null {
  if (!raw || typeof raw !== 'object') return null;
  const q = raw as Record<string, unknown> & { lines?: unknown[]; business?: Record<string, unknown> };
  return {
    ...(q as unknown as PublicQuote),
    vat_rate: num(q.vat_rate),
    subtotal_amount: num(q.subtotal_amount),
    vat_amount: num(q.vat_amount),
    total_amount: num(q.total_amount),
    business: {
      name: (q.business?.name as string) ?? null,
      logo_url: (q.business?.logo_url as string) ?? null,
      phone: (q.business?.phone as string) ?? null,
      vat_number: (q.business?.vat_number as string) ?? null,
    },
    lines: (q.lines ?? []).map((l) => {
      const line = l as Record<string, unknown>;
      return {
        id: String(line.id),
        description: String(line.description ?? ''),
        quantity: num(line.quantity),
        unit_amount: num(line.unit_amount),
        line_total: num(line.line_total),
        is_optional: Boolean(line.is_optional),
        is_selected: Boolean(line.is_selected),
      };
    }),
  };
}

export const publicQuoteKey = (token: string | undefined) => ['public-quote', token] as const;

export function usePublicQuote(token: string | undefined) {
  const valid = Boolean(token && TOKEN_RE.test(token));
  return useQuery({
    queryKey: publicQuoteKey(token),
    enabled: valid,
    retry: 1,
    staleTime: 30_000,
    queryFn: async (): Promise<PublicQuote | null> => {
      const { data, error } = await rpcClient.rpc('get_public_quote', { _token: token! });
      if (error) throw new Error(error.message);
      return normalise(data);
    },
  });
}

export interface RespondInput {
  token: string;
  action: 'accept' | 'decline';
  name?: string;
  reason?: string;
  selectedLineIds?: string[];
}

/**
 * Accept or decline, then (best effort) ask the `notify-quote-response` edge
 * function to email the business. The notification is keyed only by the token
 * and checks the quote's real state server-side.
 */
export function useRespondToQuote() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: RespondInput): Promise<PublicQuote | null> => {
      const { data, error } = await rpcClient.rpc('respond_to_quote', {
        _token: input.token,
        _action: input.action,
        _name: input.name ?? null,
        _reason: input.reason ?? null,
        _selected_line_ids: input.selectedLineIds ?? [],
      });
      if (error) throw new Error(error.message);
      try {
        await supabase.functions.invoke('notify-quote-response', { body: { token: input.token } });
      } catch (notifyError) {
        console.error('Quote response notification failed:', notifyError);
      }
      return normalise(data);
    },
    onSuccess: (quote, input) => {
      if (quote) queryClient.setQueryData(publicQuoteKey(input.token), quote);
    },
  });
}
